/**
 * CRM通知設定管理用の API hooks
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

/**
 * CRM通知ルールの型
 */
export type CrmNotificationRule = {
  id: string;
  eventType: string;
  eventTypeLabel: string;
  isEnabled: boolean;
  slackChannelId: string | null;
  slackChannelName: string | null;
};

/**
 * CRM通知設定の型
 */
export type CrmNotificationSettings = {
  id: string;
  slackEnabled: boolean;
  emailEnabled: boolean;
  rules: CrmNotificationRule[];
};

/**
 * CRM通知設定APIレスポンスの型
 */
type CrmNotificationSettingsResponse = {
  id: string;
  slackEnabled: boolean;
  emailEnabled: boolean;
  rules: CrmNotificationRule[];
  createdAt: string;
  updatedAt: string;
};

/**
 * CRM通知設定を取得する hook
 * @return {object} settings, loading, error, refetch
 */
export function useCrmNotificationSettings(): {
  settings: CrmNotificationSettings | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [settings, setSettings] = useState<CrmNotificationSettings | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<CrmNotificationSettingsResponse>(
        "/crm/notification-settings",
      );
      setSettings({
        id: response.id,
        slackEnabled: response.slackEnabled,
        emailEnabled: response.emailEnabled,
        rules: response.rules,
      });
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return { settings, loading, error, refetch: fetchSettings };
}

/**
 * CRM通知設定のマスタートグルを更新する hook
 * @return {object} updateSettings, loading
 */
export function useUpdateCrmNotificationSettings(): {
  updateSettings: (
    payload: Partial<
      Pick<CrmNotificationSettings, "slackEnabled" | "emailEnabled">
    >,
  ) => Promise<CrmNotificationSettings>;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);

  const updateSettings = useCallback(
    async (
      payload: Partial<
        Pick<CrmNotificationSettings, "slackEnabled" | "emailEnabled">
      >,
    ): Promise<CrmNotificationSettings> => {
      setLoading(true);

      try {
        const response =
          await restClient.patch<CrmNotificationSettingsResponse>(
            "/crm/notification-settings",
            payload,
          );
        return {
          id: response.id,
          slackEnabled: response.slackEnabled,
          emailEnabled: response.emailEnabled,
          rules: response.rules,
        };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateSettings, loading };
}

/**
 * CRM通知ルールを個別に更新する hook
 * @return {object} updateRule, loading
 */
export function useUpdateCrmNotificationRule(): {
  updateRule: (
    ruleId: string,
    payload: Partial<
      Pick<
        CrmNotificationRule,
        "isEnabled" | "slackChannelId" | "slackChannelName"
      >
    >,
  ) => Promise<CrmNotificationRule>;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);

  const updateRule = useCallback(
    async (
      ruleId: string,
      payload: Partial<
        Pick<
          CrmNotificationRule,
          "isEnabled" | "slackChannelId" | "slackChannelName"
        >
      >,
    ): Promise<CrmNotificationRule> => {
      setLoading(true);

      try {
        const response = await restClient.patch<CrmNotificationRule>(
          `/crm/notification-settings/rules/${ruleId}`,
          payload,
        );
        return response;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateRule, loading };
}
